/**
 * ตัว sync แถว ledger สต๊อกของร้านหนึ่งร้าน (local mode) — ไม่ผูก Vue / axios
 * แยกออกมาเพื่อให้ scripts/test-stock-checkpoint.mjs รันโค้ดตัวจริงกับ server ปลอมได้
 *
 * Full load  : /sync ไม่ส่ง checkpoint (ได้ H ก่อน) -> /all ทุกหน้า -> commit ทีเดียว
 * Incremental: /sync { checkpoint } -> ใส่ rows ทับ / ลบ deleted -> commit แบบ compare-and-set
 *
 * กติกาที่กันตัวเลขผิดเงียบ ๆ
 *   - อ่าน checkpoint "ก่อน" ดึงแถว (server อ่านทุกอย่างแบบ consistent) แถวที่ได้จึงครอบคลุมทุกอย่างก่อน H
 *   - commit แถว + meta ใน transaction เดียว และ incremental commit ได้เฉพาะเมื่อ checkpoint ในเครื่อง
 *     ยังเป็นค่าตอนเริ่มงาน (กัน sync สองงาน / สองแท็บ commit ทับกัน)
 *   - sync ของร้านเดียวกันรันทีละงาน: single-flight ในแท็บ + lock ข้ามแท็บ
 *   - dispose แล้ว (flag ถูกปิด -> ลบแคชทุกร้าน) ห้าม commit อะไรอีก
 *   - หน้าเกินเพดาน = throw ไม่ตัดทิ้งเงียบ ๆ
 */

import {
  applyChanges,
  fullLoadReason,
  ledgerKey2,
  MAX_CACHE_AGE_MS,
  type StockCacheMeta,
  type StockRow,
  type SyncResponse,
} from "./stockCheckpoint";
import type { StockDb } from "./stockTxDb";

/** bump = ทุกเครื่องโหลดใหม่หมด (runbook reset: flag ปิด -> deploy ที่ bump ค่านี้ -> force reload -> flag เปิด) */
export const STOCK_SCHEMA_VERSION = 1;

const MAX_PAGES = 5000;

export interface StockApi {
  sync(storeId: string, checkpoint?: number): Promise<SyncResponse>;
  fetchAllPage(storeId: string, cursor: string | null): Promise<{ items: StockRow[]; nextCursor: string | null }>;
}

export type StockLock = <T>(name: string, fn: () => Promise<T>) => Promise<T>;

export interface StockEngineOptions {
  storeId: string;
  api: StockApi;
  db: StockDb;
  lock?: StockLock;
  now?: () => number;
  schemaVersion?: number;
  maxCacheAgeMs?: number;
  /** เรียกทุกครั้งที่แถวในเครื่องเปลี่ยน (โหลดจากแคช / full load / incremental / บันทึกเอง) */
  onChange?: (rows: StockRow[]) => void;
}

export class StockEngineDisposedError extends Error {
  constructor() {
    super("stock engine disposed");
    this.name = "StockEngineDisposedError";
  }
}

/** lock ข้ามแท็บด้วย Web Locks ถ้ามี ไม่มีก็รันตรง ๆ (single-flight ในแท็บยังกันอยู่) */
export const browserLock: StockLock = (name, fn) => {
  const locks = typeof navigator !== "undefined" ? (navigator as any).locks : undefined;
  if (locks && typeof locks.request === "function") return locks.request(name, fn);
  return fn();
};

const sameMeta = (a: StockCacheMeta | null, b: StockCacheMeta | null) =>
  (!a && !b) ||
  (!!a &&
    !!b &&
    a.complete === b.complete &&
    a.checkpoint === b.checkpoint &&
    a.fullLoadCompletedAt === b.fullLoadCompletedAt &&
    a.schemaVersion === b.schemaVersion);

export const createStockEngine = (options: StockEngineOptions) => {
  const {
    storeId,
    api,
    db,
    lock = browserLock,
    now = () => Date.now(),
    schemaVersion = STOCK_SCHEMA_VERSION,
    maxCacheAgeMs = MAX_CACHE_AGE_MS,
    onChange,
  } = options;

  let rows = new Map<string, StockRow>();
  let meta: StockCacheMeta | null = null;
  let loadedFromDb = false;
  let disposed = false;
  let inflight: Promise<void> | null = null;

  const assertAlive = () => {
    if (disposed) throw new StockEngineDisposedError();
  };

  const publish = () => {
    if (!disposed && onChange) onChange([...rows.values()]);
  };

  const setRows = (list: StockRow[]) => {
    rows = new Map(list.map((row) => [ledgerKey2(row), { ...row, key2: ledgerKey2(row) }]));
  };

  const loadFromDb = async () => {
    const stored = await db.load(storeId);
    assertAlive();
    meta = stored.meta;
    setRows(stored.rows);
    loadedFromDb = true;
  };

  const fullLoad = async () => {
    // checkpoint ก่อน แถวทีหลัง: ทุกการเขียนที่ seq <= H อยู่ในแถวที่ดึงหลังจากนี้แน่นอน
    const head = await api.sync(storeId);
    assertAlive();
    if (head.status !== "ok") throw new Error(`stock sync: อ่าน checkpoint ไม่ได้ (${(head as any).reason || head.status})`);

    const all: StockRow[] = [];
    let cursor: string | null = null;
    let pages = 0;
    do {
      const page = await api.fetchAllPage(storeId, cursor);
      assertAlive();
      all.push(...(page.items || []));
      cursor = page.nextCursor || null;
      pages += 1;
      if (cursor && pages >= MAX_PAGES) throw new Error(`stock sync: หน้าเกิน ${MAX_PAGES} หน้า หยุดก่อน (ไม่ตัดทิ้งเงียบ ๆ)`);
    } while (cursor);

    const nextMeta: StockCacheMeta = {
      storeId,
      complete: true,
      checkpoint: head.checkpoint,
      fullLoadCompletedAt: now(),
      schemaVersion,
      rowCount: all.length,
    };
    assertAlive();
    await db.commitFullLoad(storeId, all, nextMeta);
    assertAlive();
    setRows(all);
    meta = nextMeta;
    loadedFromDb = true;
    publish();
  };

  const incremental = async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const start = meta!.checkpoint;
      const res = await api.sync(storeId, start);
      assertAlive();

      if (res.status === "reload") return fullLoad();
      if (res.status !== "ok") throw new Error("stock sync: response ไม่รู้จัก");

      const changed = res.rows || [];
      const deleted = res.deleted || [];
      if (res.checkpoint === start && changed.length === 0 && deleted.length === 0) return;

      const nextRows = applyChanges(rows, changed, deleted);
      const nextMeta: StockCacheMeta = { ...meta!, checkpoint: res.checkpoint, rowCount: nextRows.size };

      assertAlive();
      const applied = await db.commitChanges(storeId, {
        rows: changed,
        deleted,
        expectedCheckpoint: start,
        meta: nextMeta,
      });
      assertAlive();

      if (applied) {
        rows = nextRows;
        meta = nextMeta;
        publish();
        return;
      }

      // มีงานอื่น (อีกแท็บ) เขียนไปก่อน -> เชื่อของใน db แล้วลองใหม่จาก checkpoint นั้น
      await loadFromDb();
      publish();
      if (fullLoadReason(meta, now(), schemaVersion, maxCacheAgeMs)) return fullLoad();
    }
    throw new Error("stock sync: checkpoint ถูกเปลี่ยนระหว่างทางซ้ำหลายรอบ");
  };

  const run = async (forceFull: boolean) => {
    await lock(`stock-sync:${storeId}`, async () => {
      assertAlive();

      // แท็บอื่นอาจขยับแคชไปแล้ว อ่าน meta จาก db ใหม่ทุกครั้งที่ได้ lock
      const dbMeta = await db.readMeta(storeId);
      assertAlive();
      if (!loadedFromDb || !sameMeta(dbMeta, meta)) {
        await loadFromDb();
        if (!fullLoadReason(meta, now(), schemaVersion, maxCacheAgeMs)) publish(); // วาดจากแคชก่อนรอเน็ต
      }

      if (forceFull) {
        await db.clearStore(storeId);
        assertAlive();
        rows = new Map();
        meta = null;
        publish();
        return fullLoad();
      }

      if (fullLoadReason(meta, now(), schemaVersion, maxCacheAgeMs)) return fullLoad();
      return incremental();
    });
  };

  return {
    storeId,

    /** sync ปกติ (โหลดเต็มถ้าจำเป็น) งานที่ค้างอยู่ใช้ร่วมกัน */
    sync(): Promise<void> {
      if (!inflight) {
        inflight = run(false).finally(() => {
          inflight = null;
        });
      }
      return inflight;
    },

    /** ปุ่ม Reload: ล้างแคชร้านนี้แล้วโหลดใหม่หมด */
    async reload(): Promise<void> {
      if (inflight) await inflight.catch(() => {});
      inflight = run(true).finally(() => {
        inflight = null;
      });
      return inflight;
    },

    /** แถวที่เพิ่งบันทึกเอง (POST สำเร็จแล้ว) ใส่ลงเครื่องทันที รอ stream ยืนยันทีหลัง */
    async applyLocal(row: StockRow): Promise<void> {
      assertAlive();
      const key = ledgerKey2(row);
      rows = new Map(rows).set(key, { ...row, key2: key });
      publish();
      await db.putRow(storeId, { ...row, key2: key });
    },

    rows(): StockRow[] {
      return [...rows.values()];
    },

    hasCompleteCache(): boolean {
      return !!meta && meta.complete;
    },

    meta(): StockCacheMeta | null {
      return meta ? { ...meta } : null;
    },

    dispose() {
      disposed = true;
    },

    isDisposed() {
      return disposed;
    },
  };
};

export type StockEngine = ReturnType<typeof createStockEngine>;
